import EnhancedMenuItemDialogFormStateless, {
  MenuItemFormData,
} from "@/components/EnhancedMenuItemDialogForm.stateless";
import { MenuItemsTable } from "@/components/tables/MenuItemsTable";
import { MenuItem } from "@/model/entities";
import { useCategories } from "@/query/useCategories";
import { useMenuItemsByRestaurant } from "@/query/useMenuItems";
import { t } from "i18next";
import { useState } from "react";
import { toast } from "sonner";
import { getMaxOrderInCategory } from "@/utils/menuItemsUtils";

interface MenuItemsManagementTabProps {
  restaurantId: string;
}

const MenuItemsManagementTab: React.FC<MenuItemsManagementTabProps> = ({
  restaurantId,
}) => {
  const {
    data: menuItems,
    deleteMenuItem,
    updateMenuItem,
    addMenuItem,
    isLoading,
    refetch,
    toggleAvailability,
  } = useMenuItemsByRestaurant(restaurantId);
  const { data: categories } = useCategories(restaurantId);
  //   const { deleteMenuItem } = useMenuItems(restaurantId);

  const [showMenuItemDialog, setShowMenuItemDialog] = useState(false);
  const [menuItemToEdit, setMenuItemToEdit] = useState<MenuItem | undefined>(
    undefined,
  );
  // Menu item handlers
  const handleAddMenuItem = () => {
    setMenuItemToEdit(undefined);
    setShowMenuItemDialog(true);
  };

  const handleEditMenuItem = (item: MenuItem) => {
    console.log("Editing item", item);
    setMenuItemToEdit(item);
    setShowMenuItemDialog(true);
  };

  const handleCloseMenuItemDialog = () => {
    console.log("Closing dialog");
    setMenuItemToEdit(undefined);
    setShowMenuItemDialog(false);
  };

  const handleDeleteMenuItem = async (item: MenuItem) => {
    if (!item?.id) return;
    try {
      await deleteMenuItem.mutateAsync(item.id);
      toast.success(t("menuItem.deletedSuccess", "הפריט נמחק בהצלחה"));
      refetch();
    } catch (err) {
      console.error("Error deleting menu item", err);
      toast.error(t("menuItem.deletedError", "שגיאה במחיקת פריט"));
    }
  };

  const handleUpdateMenuItem = async (id: string, item: MenuItemFormData) => {
    if (!id) return;
    try {
      // Ensure isAvailable is included in updatedItem
      await updateMenuItem.mutateAsync({ id, updatedItem: item });
      toast.success(t("menuItem.updatedSuccess", "הפריט עודכן בהצלחה"));
      refetch();
    } catch (err) {
      console.error("Error updating menu item", err);
      toast.error(t("menuItem.updatedError", "שגיאה בעדכון פריט"));
    }
  };

  const createMenuItem = async (item: MenuItemFormData) => {
    try {
      const maxOrder = getMaxOrderInCategory(menuItems || [], item.categoryId);
      item = { ...item, order: maxOrder + 1 };
      console.log("Creating menu item", item);
      await addMenuItem.mutateAsync(item);
      toast.success(t("menuItem.createdSuccess", "הפריט נוצר בהצלחה"));
      handleCloseMenuItemDialog();
    } catch (err) {
      console.error("Error creating menu item", err);
      toast.error(t("menuItem.createdError", "שגיאה ביצירת פריט"));
    }
  };

  
  const handleToggleAvailability = async (item: MenuItem) => {
    if (!item?.id) return;
    try {
      const newAvailability = !item.isAvailable;
      await toggleAvailability.mutateAsync({
        itemId: item.id,
        isAvailable: newAvailability,
      });
      toast.success(
        newAvailability
          ? t("menuItem.unhiddenSuccess", "הפריט הוצג בתפריט")
          : t("menuItem.hiddenSuccess", "הפריט הוסתר מהתפריט")
      );
      refetch();
    } catch (err) {
      console.error("Error toggling menu item availability", err);
      toast.error(t("menuItem.toggleAvailabilityError", "שגיאה בשינוי נראות הפריט"));
    }
  };

  if (isLoading) {
    return <div>{t("loading", "טוען...")}</div>;
  }
  return (
    <>
      <MenuItemsTable
        menuItems={menuItems || []}
        categories={categories || []}
        onAdd={handleAddMenuItem}
        onEdit={handleEditMenuItem}
        onDelete={handleDeleteMenuItem}
        onToggleAvailability={handleToggleAvailability}
      />

      <EnhancedMenuItemDialogFormStateless
        open={showMenuItemDialog}
        mode={menuItemToEdit ? "edit" : "add"}
        defaultValues={menuItemToEdit}
        categories={categories || []}
        onAdd={createMenuItem}
        onUpdate={handleUpdateMenuItem}
        onCancel={handleCloseMenuItemDialog}
      />
    </>
  );
};

export default MenuItemsManagementTab;
